import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantDocumentService } from "../services/tenantDocumentService";
import { tenantDocumentDisplayId } from "./TenantDocumentCommonStore";
import { TenantDocument } from "../../../api/graphql";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { DocumentProcessingStatus } from "../../../api/graphql";
import { ChunkingStrategy } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class TenantDocumentViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantDocumentService;
  tenantDocumentInstance: TenantDocument | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  descriptionValue: string = "";
  fileFiles: CrudRows = {};
  fileTypeValue: string = "";
  fileSizeValue: number = 0;
  statusValue: DocumentProcessingStatus = "" as DocumentProcessingStatus;
  chunkCountValue: number | undefined = undefined;
  uploadedAtValue: Date | null = null;
  processedAtValue: Date | null = null;
  processingErrorValue: string = "";
  contentTypeValue: string = "";
  chunkingStrategyValue: ChunkingStrategy = "" as ChunkingStrategy;
  chunkingSeparatorRegexValue: string = "";
  chunkingMaxTokensValue: number | undefined = undefined;
  chunkingOverlapTokensValue: number | undefined = undefined;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantDocumentById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantDocumentOne({ id });
    this.tenantDocumentInstance = result.tenantDocumentOne;
    if (this.tenantDocumentInstance) {
      this.loadValues(this.tenantDocumentInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantDocumentInstance) {
      return tenantDocumentDisplayId(
        this.tenantDocumentInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantDocumentSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getStatusOptions() {
    return this.langStore.DocumentProcessingStatusOptions;
  }

  getChunkingStrategyOptions() {
    return this.langStore.ChunkingStrategyOptions;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "TenantDocument",
      fieldName: fieldName,
      instanceId: this.tenantDocumentInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "TenantDocument",
        fieldName: fieldName,
        instanceId: this.tenantDocumentInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantDocumentInstance = null;
    this.nameValue = "";
    this.descriptionValue = "";
    this.fileFiles = {};
    this.fileTypeValue = "";
    this.fileSizeValue = 0;
    this.statusValue = "" as DocumentProcessingStatus;
    this.chunkCountValue = undefined;
    this.uploadedAtValue = null;
    this.processedAtValue = null;
    this.processingErrorValue = "";
    this.contentTypeValue = "";
    this.chunkingStrategyValue = "" as ChunkingStrategy;
    this.chunkingSeparatorRegexValue = "";
    this.chunkingMaxTokensValue = undefined;
    this.chunkingOverlapTokensValue = undefined;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenantDocument() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("tenantDocument_update");
  }

  editTenantDocument() {
    const id = this.tenantDocumentInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenantDocument/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenantDocument(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenantDocument(): any {
    yield this.close();
  }

  loadValues(instance: TenantDocument) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.descriptionValue = instance.description || "";
    this.fileFiles = instance.file_data
      ? {
          [instance.file_data.name]: {
            name: instance.file_data.name,
            formattedSize: formatBytes(instance.file_data?.size || 0),
            mimetype: instance.file_data?.mimetype,
            id: instance.file_data.name,
            __status__: "original",
          },
        }
      : {};
    this.fileTypeValue = instance.fileType;
    this.fileSizeValue = instance.fileSize;
    this.statusValue = instance.status;
    this.chunkCountValue = instance.chunkCount || undefined;
    this.uploadedAtValue = new Date(instance.uploadedAt);
    this.processedAtValue = instance.processedAt
      ? new Date(instance.processedAt)
      : null;
    this.processingErrorValue = instance.processingError || "";
    this.contentTypeValue = instance.contentType || "";
    this.chunkingStrategyValue = instance.chunkingStrategy;
    this.chunkingSeparatorRegexValue = instance.chunkingSeparatorRegex || "";
    this.chunkingMaxTokensValue = instance.chunkingMaxTokens || undefined;
    this.chunkingOverlapTokensValue =
      instance.chunkingOverlapTokens || undefined;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantDocumentById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
